import React, {useEffect} from 'react';
import {SupplementChip} from 'client/components/QuestionTypes/shared/DraggableChip';
import {useShallow} from 'zustand/react/shallow';
import type {DragEndEvent, DragOverEvent, DragStartEvent} from '@dnd-kit/core';
import DndContextWrapper from 'client/components/QuestionTypes/shared/DndContextWrapper';
import {useQuestionStore} from 'client/components/QuestionTypes/V2/useQuestionStores';
import useQuestionAndDrafts from '../../V2/useQuestionAndDrafts.hook';
import ItemList from './ItemList.react';
import CorrectAnswerSection from './CorrectAnswerSection.react';
interface Props {
  question: Record<string, any>;
}

const OrderingBankQuestion = ({question}: Props) => {
  const {initialize, saveDraftGuess} = useQuestionAndDrafts(question);

  const useStoreHook = useQuestionStore<'ordering'>(question);

  const {
    questionInStore,
    items,
    setIsDragging,
    setActiveItemId,
    setHoveredDividerId,
    moveItemToPosition,
    setDraggedItemId,
    setPreviewTargetIndex,
    isSubmitted
  } = useStoreHook(
    useShallow((state) => ({
      questionInStore: state.question,
      items: state.items,
      setIsDragging: state.setIsDragging,
      setActiveItemId: state.setActiveItem,
      setHoveredDividerId: state.setHoveredDividerId,
      moveItemToPosition: state.moveItemToPosition,
      setDraggedItemId: state.setDraggedItemId,
      setPreviewTargetIndex: state.setPreviewTargetIndex,
      isSubmitted: state.isSubmitted
    }))
  );

  const onDragStart = (event: DragStartEvent) => {
    setIsDragging(true);
    setActiveItemId(null);
    setDraggedItemId(event.active.id as string);
  };

  const onDragOver = (event: DragOverEvent) => {
    const {over} = event;
    if (over && over.id.toString().startsWith('divider-')) {
      const dividerId = over.id.toString();
      const targetIndex = parseInt(dividerId.split('-')[1], 10);
      setHoveredDividerId(dividerId);
      setPreviewTargetIndex(targetIndex);
    } else {
      setHoveredDividerId(null);
      setPreviewTargetIndex(null);
    }
  };

  const onDrop = (event: DragEndEvent) => {
    const {active, over} = event;
    setIsDragging(false);
    setHoveredDividerId(null);
    setDraggedItemId(null);
    setPreviewTargetIndex(null);

    if (!over || !over.id.toString().startsWith('divider-')) return;

    const dividerId = over.id.toString();
    const targetIndex = parseInt(dividerId.split('-')[1], 10);
    moveItemToPosition(active.id as string, targetIndex);
    saveDraftGuess();
  };

  useEffect(() => {
    if (question && question.size > 0) {
      initialize(question);
    }
  }, [question, initialize]);

  if (!questionInStore) return null;

  return (
    <DndContextWrapper
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDrop}
      renderOverlay={(activeId) => {
        const item = items.find((i) => i.id === activeId);
        return item ? <SupplementChip value={item.value} size='small' /> : null;
      }}
    >
      <div className='flex flex-col'>
        {/* Ordering Bank */}
        <ItemList question={questionInStore} />
        {isSubmitted && <CorrectAnswerSection question={questionInStore} />}
      </div>
    </DndContextWrapper>
  );
};

export default OrderingBankQuestion;
